// src/share/IntentShareFrames.jsx
// Shared preview frames for the intent-URL share path (no platform API call).
//
// We use these instead of the native-tweet / native-WhatsApp-image mockups when
// posting goes via web intent (twitter.com/intent/tweet, wa.me/?text=…). Why:
// intent URLs cannot attach images directly. The platform unfurls the link and
// renders a *link card* using our public page's og:image / og:title /
// og:description. The user's actual post will look like a link card, not a
// native image post — these previews show that reality so we don't oversell.
//
// To flip back to native-image previews when X_API_ENABLED=true, the four
// share surfaces ternary between these frames and the existing TwitterFrame /
// future WhatsAppNativeFrame. See sharingCenter.jsx for the gate.
//
// Used by:
//   - src/share/CompanySharePreview.jsx
//   - src/share/ProfileSharePreview.jsx
//   - src/share/TeamSharePreview.jsx
//   - src/design/PublishPreviewModal.jsx
//
// Each surface passes its own image-rendering child (CompanyShareCard,
// ProfileShareCard, TeamShareCard, or ImageComposite). The frame handles all
// the chrome — tweet card, WhatsApp bubble, link-card layout, engagement row.

import React from 'react';

// ─── Internal: the link-card subcomponent shared by both frames ─────────────
// 1.91:1 image (X & WhatsApp both crop link-card images to ~1.91:1), title,
// optional description, and the source domain row. The image is whatever
// React node the parent passes via `children` — ImageComposite, ShareCard,
// or just a plain <img>.
function LinkCardBody({ children, title, description, domain }) {
  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 bg-white">
      <div className="w-full" style={{ aspectRatio: '1.91 / 1', overflow: 'hidden' }}>
        {children}
      </div>
      <div className="px-2.5 py-1.5">
        {domain && (
          <div className="text-[9px] uppercase tracking-wide text-gray-500 truncate">
            {domain}
          </div>
        )}
        {title && (
          <div className="text-[11px] font-semibold text-gray-900 leading-tight truncate mt-0.5">
            {title}
          </div>
        )}
        {description && (
          <div className="text-[10px] text-gray-600 leading-snug mt-0.5 line-clamp-2">
            {description}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── X (Twitter) intent-URL preview ─────────────────────────────────────────
// Looks like a tweet whose only attachment is a link card — i.e. exactly what
// the user's followers will see when the user shares via twitter.com/intent.
//
// Props:
//   biz         { name, handle, initials } — display identity
//   caption     string — the user's tweet body (already truncated to ~250 chars)
//   title       string — link card title (defaults to biz name)
//   description string — link card description (optional, omit for tighter look)
//   domain      string — defaults to "ehandshake.com"
//   children    ReactNode — image content rendered inside the 1.91:1 link card
export function XLinkCardFrame({
  biz,
  caption,
  title,
  description,
  domain = 'ehandshake.com',
  children,
}) {
  const safeTitle = title || (biz && biz.name) || '';
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto flex-shrink-0">
      <div className="px-2.5 pt-2 pb-1 flex items-start gap-2">
        <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-white text-[9px] font-bold shrink-0">
          {biz?.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-bold text-gray-900">{biz?.name}</span>
            <span className="text-[10px] text-gray-500">@{biz?.handle} · Just now</span>
          </div>
          <p className="text-[11px] text-gray-900 leading-snug whitespace-pre-wrap mt-0.5">
            {caption}
          </p>
        </div>
      </div>
      <div className="px-2.5 pb-1">
        <LinkCardBody title={safeTitle} description={description} domain={domain}>
          {children}
        </LinkCardBody>
      </div>
      <div className="px-8 py-1.5 flex items-center justify-between text-gray-500 text-[11px]">
        <span className="flex items-center gap-0.5">💬 <span>5</span></span>
        <span className="flex items-center gap-0.5">🔄 <span>12</span></span>
        <span className="flex items-center gap-0.5">🤍 <span>47</span></span>
        <span className="flex items-center gap-0.5">📤</span>
      </div>
    </div>
  );
}

// ─── WhatsApp intent-URL preview ────────────────────────────────────────────
// Looks like an outgoing WhatsApp message with a link unfurl. The whole share
// flow on WhatsApp is "send to a contact / group / Status" — we render the
// outgoing-message style (right-aligned, light-green #DCF8C6 bubble, classic
// chat wallpaper background).
//
// Props mirror XLinkCardFrame exactly. `caption` here is the message text the
// user composed in our Sharing Center — it goes ABOVE the link card (matches
// how WhatsApp renders text + URL in the same outgoing message).
export function WhatsAppMessageFrame({
  biz,
  caption,
  title,
  description,
  domain = 'ehandshake.com',
  children,
}) {
  const safeTitle = title || (biz && biz.name) || '';
  return (
    // flex-shrink-0 prevents the preview-area flex parent from compressing
    // this wrapper down to fit a short container — without it, the outer
    // overflow-hidden clips the bottom of the bubble's content (caption +
    // timestamp) and no scrollbar appears (the bubble looks like it 'fits'
    // because flex-shrink lies about its height).
    <div
      className="rounded-lg shadow-sm overflow-hidden max-w-[340px] mx-auto flex-shrink-0"
      style={{
        background: '#ECE5DD', // classic WhatsApp chat wallpaper tone
        padding: '14px 10px',
      }}
    >
      <div className="flex justify-end">
        <div
          className="rounded-lg px-2 pt-2 pb-1.5"
          style={{
            background: '#DCF8C6', // outgoing-bubble green
            maxWidth: '88%',
            boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
          }}
        >
          {/* Link card sits at the top of the bubble; below it is the caption,
              then a tiny timestamp + double-check row. Matches WhatsApp's
              outgoing-message-with-link layout. */}
          <LinkCardBody title={safeTitle} description={description} domain={domain}>
            {children}
          </LinkCardBody>
          {caption && (
            <p className="text-[12px] text-gray-900 leading-snug whitespace-pre-wrap mt-1.5">
              {caption}
            </p>
          )}
          <div className="flex items-center justify-end gap-1 mt-0.5">
            <span className="text-[9px] text-gray-500">Just now</span>
            <span className="text-[10px]" style={{ color: '#34B7F1' /* WA blue ticks */ }}>
              ✓✓
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default { XLinkCardFrame, WhatsAppMessageFrame };
